/* eslint-disable */
import React, { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Box, Typography } from '@mui/material';
import { Form, Spin } from 'antd';
import _, { isEmpty } from 'lodash';

import LightTable from './LeaseUtils/LightTable';
import { useSendControlDataMutation } from 'store/features/api/apiSlice';
import { useLocation } from 'react-router-dom';
import { formatNumber } from './LeaseUtils/helper';

const LeaseDetailsShowAll = forwardRef((_props, ref) => {
	const form = Form.useFormInstance();
	const { state } = useLocation();
	const [sendControlDataForShowAll, { data: generalLedgerData, isLoading: isSendControlDataLoading }] = useSendControlDataMutation();
	const [previousPayload, setPreviousPayload] = useState(null);

	const loadData = async () => {
		const payloadData = {
			leaseId: form.getFieldValue('leaseId'),
		};

		// Check if the payload is the same as the previous call
		if (previousPayload && previousPayload.leaseId === payloadData.leaseId && previousPayload.processCode === state?.processCode) {
			// console.log('Skipping API call - same inputs as previous call');
			return;
		}

		const convertJson = JSON.stringify(payloadData);
		const convertBase64 = btoa(convertJson);

		const formattedPayload = {
			data: convertBase64,
			processCode: state?.processCode,
			scriptName: 'lease-controller-schedule-get-findata',
			payloadData: payloadData,
		};

		// Store the current payload for future comparison
		setPreviousPayload({
			...payloadData,
			processCode: state?.processCode,
		});

		await sendControlDataForShowAll(formattedPayload);
	};
	useImperativeHandle(ref, () => ({
		loadData,
	}));
	// useEffect(() => {
	// 	loadData();
	// }, []);

	const columnData = useMemo(
		() => [
			{
				id: 'recordDocId',
				width: '5%',
				align: 'center',
				accessor: (data) => data?.latestProcessGroup?.recordDocId,
				cellAlign: 'center',
				Header: 'Document No',
				cellSx: { fontSize: '13px' },
			},
			{
				id: 'recordInputDate',
				width: '5%',
				align: 'center',
				accessor: (data) => {
					return dayjs(data?.latestProcessGroup?.recordInputDate?.dateString).format('YYYY-MM-DD');
				},
				cellAlign: 'center',
				Header: 'Document Date',
				cellSx: { fontSize: '13px' },
			},

			{
				id: 'referenceId',
				width: '5%',
				align: 'center',
				accessor: (data) => data?.latestProcessGroup?.serialNumber,
				cellAlign: 'center',
				Header: 'Reference Id',
				cellSx: { fontSize: '13px' },
			},
			{
				id: 'recordCreationDateTime',
				width: '5%',
				align: 'center',
				accessor: (data) => {
					return dayjs(data?.latestProcessGroup?.creationDateTime?.dateString).format('YYYY-MM-DD');
				},
				cellAlign: 'center',
				Header: 'Reference Date',
				cellSx: { fontSize: '13px' },
			},
			{
				id: 'description',
				width: '5%',
				align: 'left',
				accessor: (data) => data?.description,
				cellAlign: 'left',
				Header: 'Description',
				cellSx: { fontSize: '13px' },
			},
			{
				id: 'processStatus',
				width: '5%',
				align: 'center',
				accessor: (data) => data?.latestProcessGroup?.processStatus,
				cellAlign: 'center',
				Header: 'Type',
				cellSx: { fontSize: '13px' },
			},

			{
				id: 'total',
				width: '5%',
				align: 'right',
				accessor: 'total',
				cellAlign: 'center',
				Header: 'Amount',
				Cell: (cell) => {
					const debit = cell?.row?.original?.debit ? parseFloat(cell?.row?.original?.debit) : 0;
					const credit = cell?.row?.original?.credit ? parseFloat(cell?.row?.original?.credit) : 0;
					const total = isNaN(debit) ? 0 : debit - (isNaN(credit) ? 0 : credit);

					return (
						<Typography align="right" sx={{ fontSize: '13px !important' }}>
							{total !== 0 ? (total > 0 ? formatNumber(total, 2) : '(' + formatNumber(Math.abs(total), 2) + ')') : ''}
						</Typography>
					);
				},
			},

			{
				id: 'comulativeAmount',
				width: '5%',
				align: 'right',
				accessor: 'comulativeAmount',
				cellAlign: 'right',
				Header: 'Cumulative Amount',
				cellSx: { fontSize: '13px' },
			},
		],
		[]
	);

	const headSequence = [
		'RoU Asset - Initial Recognition',
		'RoU Asset - Modification',
		'RoU Asset - Pre-payment',
		'RoU Asset - Upfront Payment',
		'RoU Asset - Indirect Costs',
		'RoU Asset - Cash Incentive',
		'RoU Asset - Knock Off',
		'Security Deposit',
		'Lease Liability - Initial Recognition',
		'Lease Liability - Modification',
		'Lease Liability - Payment',
		'Lease Liability - Interest',
		'Interest Expense',
		'Depreciation - RoU',
		'Accum Depreciation - RoU',
		'Accum Depreciation - RoU - Knock Off',
		'A/P Control Ledger - Pre-payment',
		'A/P Control Ledger - Upfront Payment',
		'A/P Control Ledger - Indirect Costs',
		'A/P Control Ledger - Security Deposit',
		'A/P Control Ledger - Payment',
		'A/R Control Ledger - Cash Incentive',
		'Interest Expense - Interest',
		'Depreciation - RoU - Depreciation',
		'Accum Depreciation - RoU - Depreciation',
	];

	const groupDataByHead = (data) => {
		// console.log('🚀 ~ groupDataByHead ~ data:', data);
		if (!Array.isArray(data)) return null;

		// First, group the data by the 'head' field
		const grouped = _.groupBy(data, 'head');

		// Create an ordered result based on headSequence
		const result = headSequence
			.filter((head) => grouped[head]) // Only include heads that exist in the data
			.map((head) => {
				const values = grouped[head];
				let runningTotal = 0;

				// Calculate running totals and format data
				const indexedValues = values.map((item, index) => {
					const { head, ...rest } = item;

					// Calculate the value for this row (debit - credit)
					const debit = parseFloat(item.debit || 0);
					const credit = parseFloat(item.credit || 0);
					const rowValue = debit - credit;

					// Update running total
					runningTotal += rowValue;

					// Format the cumulative amount
					const formattedCumulative =
						runningTotal !== 0
							? runningTotal > 0
								? formatNumber(runningTotal, 2)
								: '(' + formatNumber(Math.abs(runningTotal), 2) + ')'
							: '';

					return {
						...rest,
						index,
						comulativeAmount: formattedCumulative,
					};
				});

				return {
					head,
					value: indexedValues,
				};
			});

		// console.log('🚀 ~ groupDataByHead ~ result:', result);
		return result;
	};
	const filteredData = Array.isArray(generalLedgerData?.responseData)
		? generalLedgerData?.responseData?.filter((item) => {
				return item?.cancelStatus?.toLowerCase() !== 'cancelled';
			})
		: [];
	const groupedData = useMemo(() => groupDataByHead(filteredData), [filteredData]);

	return (
		<Box>
			<Spin spinning={isSendControlDataLoading || groupedData === null} style={{ maxHeight: '100%' }}>
				{generalLedgerData?.responseData?.length !== 0 ? (
					<div className="lease-details-tables">
						{Array.isArray(groupedData) && groupedData.length > 0
							? groupedData.map((group, groupIndex) => (
									<div key={groupIndex}>
										<Typography
											variant="h6"
											sx={{ fontSize: '16px !important', fontWeight: 500, color: '#000000', mb: '10px', pl: '11px' }}
										>
											{group.head}
										</Typography>
										<div className="head-group-table" style={{ marginBottom: '30px' }}>
											<LightTable
												data={isEmpty(group.value) ? [] : group.value}
												columns={columnData}
												emptyMessage="No data available for this head"
												headerCellSx={{
													backgroundColor: '#EDF7FF !important',
													color: 'black',
													fontWeight: 500,
													height: '3.5rem',
													padding: '0.75rem',
												}}
												tableContainerSx={{
													border: '1px solid #b5b5b5',
													borderRadius: '4px',
													fontsize: '13px',
													// mb: 2,
													'& .MuiTableHead-root th': {
														backgroundColor: '#EDF7FF !important',
														color: '#000000',
													},
												}}
											/>

											{/* <Divider /> */}
										</div>
										{/* </CardAccordion> */}
									</div>
								))
							: generalLedgerData?.length === 0 &&
								groupedData?.length === 0 &&
								!isSendControlDataLoading && (
									<div style={{ textAlign: 'center', margin: '20px' }}>
										<Typography>No lease data available</Typography>
									</div>
								)}
					</div>
				) : (
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
						}}
					>
						No Ledger Available Right Now
					</Box>
				)}
			</Spin>
		</Box>
	);
});
LeaseDetailsShowAll.displayName = 'LeaseDetailsShowAll';

export default LeaseDetailsShowAll;
